import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'sonner';
const BradaChat = React.lazy(() => import('./components/BradaChat'));
import QuickNotes from './components/QuickNotes';
import { ThemeProvider } from './contexts/ThemeContext';
import { GlobalContextProvider } from './contexts/GlobalContextProvider';
import { PlanProvider, usePlan } from './contexts/PlanContext';

// Auth Pages
import LoginPageApple from './app/login/LoginPageApple';
import SignupPageApple from './app/signup/SignupPageApple';
import ForgotPasswordPageApple from './app/forgot-password/ForgotPasswordPageApple';

// Main Pages
import DashboardPageApple from './app/dashboard/DashboardPageApple';
import ProjectsPageApple from './app/projects/ProjectsPageApple';
import ProjectDetailPage from './app/projects/ProjectDetailPage';
import BasicTranslationPageApple from './app/translation/BasicTranslationPageApple';
import MTPEPageApple from './app/mtpe/MTPEPageApple';
import MTPEProPageApple from './app/mtpe-pro/MTPEProPageApple';
import CATToolPageApple from './app/cat/CATToolPageApple';
import ReviewPageApple from './app/review/ReviewPageApple';
import TMPageApple from './app/tm/TMPageApple';
import GlossaryPageApple from './app/glossary/GlossaryPageApple';
import TQAPageApple from './app/tqa/TQAPageApple';
import SettingsPageApple from './app/settings/SettingsPageApple';
import ActivityPageApple from './app/activity/ActivityPageApple';
import PricingPageApple from './app/pricing/PricingPageApple';
import PaymentStatusPage from './app/payment-status/PaymentStatusPage';
import TeamManagementPage from './pages/TeamManagementPage';
import AssignmentsPage from './app/assignments/AssignmentsPage';

// Admin Page
import AdminPage from './pages/admin/AdminPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import BillingPage from './pages/BillingPage';
import UsagePage from './pages/UsagePage';
import AffiliatePage from './pages/AffiliatePage';

// Auth helper
const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Redeem route — preserves code param then redirects to /billing
const RedeemRedirect = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';
  return <Navigate to={`/billing${code ? `?code=${encodeURIComponent(code)}` : ''}`} replace />;
};

// Login wrapper with onLogin handler
const LoginWrapper = () => {
  const navigate = useNavigate();
  const { setUserAndPlan, refreshPlanInfo } = usePlan();
  
  const handleLogin = async (token, user, planInfo) => {
    localStorage.setItem('token', token);
    if (user) localStorage.setItem('user', JSON.stringify(user));
    if (planInfo) localStorage.setItem('plan_info', JSON.stringify(planInfo));
    setUserAndPlan(user, planInfo);
    // Fetch fresh plan info after login
    await refreshPlanInfo();
    window.location.href = '/';
  };

  return <LoginPageApple onLogin={handleLogin} />;
};

// Signup wrapper with onLogin handler
const SignupWrapper = () => {
  const navigate = useNavigate();
  const { setUserAndPlan, refreshPlanInfo } = usePlan();
  const [searchParams] = useSearchParams();

  const handleLogin = async (token, user, planInfo) => {
    localStorage.setItem('token', token);
    if (user) localStorage.setItem('user', JSON.stringify(user));
    if (planInfo) localStorage.setItem('plan_info', JSON.stringify(planInfo));
    setUserAndPlan(user, planInfo);
    // Fetch fresh plan info after signup
    await refreshPlanInfo();
    // Read plan param from URL (set by marketing site CTAs)
    const planParam = searchParams.get('plan');
    if (planParam) {
      // Store plan intent — used after disclaimer is accepted
      localStorage.setItem('post_disclaimer_redirect', '/pricing');
      localStorage.setItem('signup_plan_intent', planParam);
    }
    // Always go to / with new_signup flag — disclaimer shows first
    window.location.href = '/?new_signup=true';
  };

  return <SignupPageApple onLogin={handleLogin} />;
};


// Startup migration: clear legacy/stale instruction keys that can cause infinite fetch loops
try {
  ['selectedInstruction', 'contextInstruction', 'instructionId', 'globalContext'].forEach(k => localStorage.removeItem(k));
} catch (e) {}

// Handle impersonation token from URL
const impersonateParams = new URLSearchParams(window.location.search);
const impersonateToken = impersonateParams.get('impersonate_token');
if (impersonateToken) {
  const adminToken = localStorage.getItem('token');
  if (adminToken) localStorage.setItem('admin_token_backup', adminToken);
  localStorage.setItem('token', impersonateToken);
  localStorage.removeItem('user');
  localStorage.removeItem('plan_info');
  const API = process.env.REACT_APP_BACKEND_URL;
  Promise.all([
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${impersonateToken}` } }),
    fetch(`${API}/api/pricing/plan-info`, { headers: { Authorization: `Bearer ${impersonateToken}` } })
  ]).then(async ([meRes, planRes]) => {
    if (meRes.ok) localStorage.setItem('user', JSON.stringify(await meRes.json()));
    if (planRes.ok) localStorage.setItem('plan_info', JSON.stringify(await planRes.json()));
  }).catch(console.error).finally(() => {
    window.location.replace('/');
  });
}

function App() {
  const [user, setUser] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [planInfo, setPlanInfo] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('plan_info')); } catch { return null; }
  });
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('plan_info');
    localStorage.removeItem('user_avatar');
    setUser(null);
    setPlanInfo(null);
    window.location.href = '/login';
  };
  const handlePlanUpdate = (newPlanInfo) => {
    setPlanInfo(prev => ({ ...prev, ...newPlanInfo }));
    localStorage.setItem('plan_info', JSON.stringify(newPlanInfo));
  };

  // Always verify token on mount — catches expired tokens even when user is cached in localStorage.
  // If 401: logout immediately so no page renders with a dead session.
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API}/api/pricing/plan-info`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(async ([meRes, planRes]) => {
      if (meRes.status === 401) {
        handleLogout();
        return;
      }
      if (meRes.ok) {
        const meData = await meRes.json();
        setUser(meData);
        localStorage.setItem('user', JSON.stringify(meData));
      }
      if (planRes.ok) {
        const planData = await planRes.json();
        setPlanInfo(planData);
        localStorage.setItem('plan_info', JSON.stringify(planData));
      }
    }).catch(console.error);
  }, []);

  // Read new_signup flag from URL — set by SignupWrapper after signup
  const _urlParams = new URLSearchParams(window.location.search);
  const _isNewSignup = _urlParams.get('new_signup') === 'true';
  const cp = { user, planInfo, onLogout: handleLogout, onPlanUpdate: handlePlanUpdate, isNewSignup: _isNewSignup };
  return (
    <ThemeProvider>
      <PlanProvider>
        <GlobalContextProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<PublicRoute><LoginWrapper /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><SignupWrapper /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPasswordPageApple />} />
              
              {/* Protected Routes */}
              <Route path="/" element={<ProtectedRoute><DashboardPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute><ProjectsPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage {...cp} /></ProtectedRoute>} />
              <Route path="/translation" element={<ProtectedRoute><BasicTranslationPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/mtpe" element={<ProtectedRoute><MTPEPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/mtpe-pro" element={<ProtectedRoute><MTPEProPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/cat" element={<ProtectedRoute><CATToolPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/review" element={<ProtectedRoute><ReviewPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/tm" element={<ProtectedRoute><TMPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/glossary" element={<ProtectedRoute><GlossaryPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/tqa" element={<ProtectedRoute><TQAPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/activity" element={<ProtectedRoute><ActivityPageApple {...cp} /></ProtectedRoute>} />
              <Route path="/payment-status" element={<ProtectedRoute><PaymentStatusPage /></ProtectedRoute>} />
              <Route path="/pricing" element={<ProtectedRoute><PricingPageApple {...cp} /></ProtectedRoute>} />
              
              <Route path="/team" element={<ProtectedRoute><TeamManagementPage {...cp} /></ProtectedRoute>} />
              <Route path="/assignments" element={<ProtectedRoute><AssignmentsPage {...cp} /></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage {...cp} /></ProtectedRoute>} />
              <Route path="/redeem" element={<ProtectedRoute><RedeemRedirect /></ProtectedRoute>} />
              <Route path="/usage" element={<ProtectedRoute><UsagePage {...cp} /></ProtectedRoute>} />
              <Route path="/referral" element={<ProtectedRoute><AffiliatePage {...cp} /></ProtectedRoute>} />
              <Route path="/accept-invitation" element={<ProtectedRoute><AcceptInvitationPage user={user} {...cp} /></ProtectedRoute>} />
              {/* Admin Route */}
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              
              {/* Catch all - redirect to dashboard or login */}
              <Route path="*" element={<Navigate to={isAuthenticated() ? "/" : "/login"} replace />} />
            </Routes>
            
            <Toaster
              position="top-right"
              richColors
              closeButton
              toastOptions={{
                style: {
                  borderRadius: '12px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Text, sans-serif',
                }
              }}
            />
            {isAuthenticated() && <Suspense fallback={null}><BradaChat /></Suspense>}
            {isAuthenticated() && <QuickNotes />}
          </BrowserRouter>
        </GlobalContextProvider>
      </PlanProvider>
    </ThemeProvider>
  );
}

export default App;
